<template>
  <section
    id="product-details"
    ref="productDetailsContainer"
    :class="{
      'details-optimization': shoppableRedesignOptimization,
      'uppercase-titles': !isMcom,
      'google-ai-enabled': isGoogleAiEnabledUnderProductDetails,
    }"
  >
    <ProductDetailsAiPrompts v-if="isGoogleAiAboveProductDetailsEnabled" class="margin-bottom-m" is-switch-button-available />

    <header class="header">
      <h2 class="h3 title">
        {{ productDetailsText }}
      </h2>
      <span class="product-id">
        Web ID:
        {{ isShopTheSetExpEnabled ? selectedMember : productId }}
      </span>
    </header>

    <div
      class="expandable-wrapper"
      :class="[{ open: isOpen }, spacingClass]"
      :style="isSwitchButtonAvailable ? `--blur-height: ${CLOSE_WRAPPER_HEIGHT}px` : undefined"
    >
      <!--
        a11y rules disabled: wrapper isn't interactive;
        @click used only as overlay click workaround.
      -->
      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions, vuejs-accessibility/click-events-have-key-events -->
      <div ref="wrapper" class="wrapper" :class="additionalClasses" @click="handleWrapperClick">
        <div
          v-if="isShownDefaultProductDescription || longDescriptionFromColumns?.isAvailable"
          data-auto="product-long-description-section"
          class="long-description medium"
        >
          <p v-if="isShownDefaultProductDescription" v-html="productDescription"></p>
          <div v-else-if="longDescriptionFromColumns?.isAvailable">
            <h3 v-if="longDescriptionFromColumns.title" class="h4 medium margin-bottom-xxs" v-html="longDescriptionFromColumns.title"></h3>
            <span v-html="longDescriptionFromColumns.paragraph"></span>
          </div>
        </div>

        <ul class="details">
          <template v-for="item in additionalColumns">
            <li v-if="item.isAvailable" :key="item.title">
              <h3 class="h4 medium margin-bottom-xxs" v-html="item.title"></h3>
              <span v-html="item.paragraph"></span>
            </li>
          </template>

          <li v-if="isSummaryAvailable" ref="availableColumnsArr" class="column" data-auto="product-summary-section">
            <h3 class="h4 medium margin-bottom-xxs">
              {{ featuresText }}
            </h3>
            <Summary :open-as-initial-slideout="true" />
          </li>

          <li v-if="isSizeAndFitAvailable" ref="availableColumnsArr" class="column" data-auto="size-fit-section">
            <h3 class="h4 medium margin-bottom-xxs">Size & Fit</h3>
            <SizeAndFit />
          </li>

          <li
            v-if="isIngredientsAvailable"
            ref="availableColumnsArr"
            class="column"
            data-auto="product-ingredients-section"
            data-testid="product-ingredients-section"
          >
            <h3 class="h4 medium margin-bottom-xxs capitalize">
              {{ dimensionsTitle }}
            </h3>
            <Ingredients />
          </li>

          <li
            v-if="isDimensionsAvailable"
            ref="availableColumnsArr"
            class="column"
            data-auto="product-dimensions-section"
            data-testid="product-dimensions-section"
          >
            <h3 class="h4 medium margin-bottom-xxs">Product Dimensions</h3>
            <Dimensions />
          </li>

          <li v-if="isMaterialAndCareAvailable" ref="availableColumnsArr" class="column" data-auto="materials-care-section">
            <h3 class="h4 medium margin-bottom-xxs">Materials & Care</h3>
            <MaterialsAndCare :open-as-initial-slideout="true" />
          </li>

          <li v-if="isInstructionsAvailable" ref="availableColumnsArr" class="column" data-auto="instructions-section">
            <h3 class="h4 medium margin-bottom-xxs">Instructions</h3>
            <Instructions />
          </li>

          <li v-if="isTermsAndConditionsAvailable" ref="availableColumnsArr" class="column" data-auto="terms-conditions-section">
            <h3 class="h4 medium margin-bottom-xxs">Terms & Conditions</h3>
            <TermsAndConditions />
          </li>

          <li v-if="isShippingAndReturnsAvailable" ref="availableColumnsArr" class="column" data-auto="shipping-returns-section">
            <h3 class="h4 medium margin-bottom-xxs">Shipping & Returns</h3>
            <ShippingAndReturns />
          </li>

          <li v-if="isWhiteGloveDeliveryAvailable" ref="availableColumnsArr" class="column" data-auto="white-glove-delivery-section">
            <h3 class="h4 medium margin-bottom-xxs">
              {{ whiteGloveTitle }}
            </h3>
            <WhiteGloveDelivery />
          </li>
          <li ref="genesysChatContainer" class="column" data-auto="genesys-chat-widget-section">
            <div id="rnowChatLinkContainer_type16" :class="{ 'genesys-fine-jewelry': isFineJewelryCategory }"></div>
          </li>
        </ul>
      </div>
      <!-- a11y off: click comes from inner button (bubbling) -->
      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions, vuejs-accessibility/click-events-have-key-events -->
      <div v-if="isSwitchButtonAvailable" class="switch-wrapper" :class="{ 'ai-prompts': isGoogleAiEnabled }" @click="handleButtonClick">
        <button class="switch link-med" :class="{ hide: isGoogleAiEnabled }">
          {{ buttonText }}
        </button>
      </div>
    </div>

    <ProductDetailsAiPrompts
      v-if="isGoogleAiEnabledUnderProductDetails"
      :view-details-text="viewDetailsText"
      :is-switch-button-available="isSwitchButtonAvailable"
      :show-ask-brand-header="false"
      @view-full-details="handleButtonClick"
    />
    <div v-else-if="isGoogleAiEnabled" :class="{ 'margin-top-xs': !isSwitchButtonAvailable }">
      <ViewDetailsButton :text="viewDetailsText" :hide="!isSwitchButtonAvailable" @click="handleButtonClick" />
    </div>
  </section>
</template>

<script setup>
import { computed, ref, onMounted, watch, defineAsyncComponent, nextTick, toValue } from 'vue';
import { useToggle, useResizeObserver } from '@vueuse/core';
import { useProduct } from '~/modules/product/composables/useProduct';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import { useBrand } from '~/composables/useBrand';
import { useGoogleAiEligibility } from '~/composables/useGoogleAiEligibility';
import { useSlideout } from '~/composables/utils/useSlideout';
import { useProductDetailsProvide, useProductDetails } from '../composables/useProductDetails';
import { useProductDetailsAvailabilityProvide, useProductDetailsAvailability } from '../composables/useProductDetailsAvailability';
import tagViewMoreViewLessToggle from '~/features/analytics/productDetails';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';
import ViewDetailsButton from './ViewDetailsButton.vue';
import { useDivisionCategoryFlags } from '~/modules/featureControl/composables/useDivisionCategoryFlags';

import Summary from './SummaryView';
import Dimensions from './DimensionsView';
import MaterialsAndCare from './MaterialsAndCare';
import Instructions from './InstructionsView';
import TermsAndConditions from './TermsAndConditions';
import SizeAndFit from './SizeAndFit';
import WhiteGloveDelivery from './WhiteGloveDelivery';
import ShippingAndReturns from './ShippingAndReturns';
import Ingredients from './IngredientsView';
import ProductDetailsAiPrompts from './ProductDetailsAiPrompts';
import { splitTextToColumns } from '../utils/stringHelpers';

useProductDetailsProvide();
useProductDetailsAvailabilityProvide();

const { productId, productData: product } = useProduct();
const { isFineJewelryCategory } = useDivisionCategoryFlags();
const { isMcom } = useBrand();
const { isMobile } = useDeviceType();
const { bigTicketDeliveryMethods } = useCommonProductGetters();
const { productDescription, dimensionsTitle } = useProductDetails();
const whiteGloveTitle = computed(() => (toValue(bigTicketDeliveryMethods)?.length ? 'Delivery Methods' : 'White Glove Delivery'));
const { shoppableRedesignOptimization, isShopTheSetExpEnabled } = useFeatureEligibility();

const selectedMember = useSelectedMemberId();

const {
  isSummaryAvailable,
  isSizeAndFitAvailable,
  isIngredientsAvailable,
  isDimensionsAvailable,
  isMaterialAndCareAvailable,
  isInstructionsAvailable,
  isTermsAndConditionsAvailable,
  isShippingAndReturnsAvailable,
  isWhiteGloveDeliveryAvailable,
} = useProductDetailsAvailability();
const { killswitches } = useKillswitches();

const { isGoogleAiEnabledUnderProductDetails, isGoogleAiAboveProductDetailsEnabled, isGoogleAiEnabled } = useGoogleAiEligibility();
const { showSlideout } = useSlideout();

const CLOSE_WRAPPER_HEIGHT = 240;
const spacingClass = shoppableRedesignOptimization ? 'margin-top-s' : 'medium-margin-top-s small-margin-top-xs';
const wrapper = ref(null);
const genesysChatContainer = ref(null);
const isSwitchButtonAvailable = ref(false);

// For more information regarding this regExp, please refer to my comment there https://macystech.atlassian.net/browse/UMP-1742?focusedCommentId=3451197
const TITLE_REGEXP = /(?:<b>)?([A-Z0-9\s&;'’]+):?(?:<\/b>)?:?(?=\s*<br>|$)/g;
const TITLE_REGEXP_OPTIMIZED = /(?:<b>)?([A-Z0-9][A-Z0-9&;'’\s*-]{2,}):?(?:<\/b>)?:?(?=\s*<br>|$)/g;
const regExp = shoppableRedesignOptimization || killswitches.isPDPEnhancementNEnabled ? TITLE_REGEXP_OPTIMIZED : TITLE_REGEXP;

const descriptionColumns = computed(() =>
  isMcom ? [] : splitTextToColumns(productDescription.value, regExp, killswitches.isPDPEnhancementNEnabled),
);
const longDescriptionFromColumns = computed(() => descriptionColumns.value[0]);
const isShownDefaultProductDescription = computed(
  () => (descriptionColumns.value.length === 0 || !longDescriptionFromColumns.value?.isAvailable) && !!productDescription.value,
);
const additionalColumns = computed(() => (productDescription.value.length ? descriptionColumns.value.slice(1) : []));

const columnsArr = computed(() => [
  ...additionalColumns.value,
  isSummaryAvailable.value,
  isSizeAndFitAvailable.value,
  isIngredientsAvailable.value,
  isDimensionsAvailable.value,
  isMaterialAndCareAvailable.value,
  isInstructionsAvailable,
  isTermsAndConditionsAvailable,
  isShippingAndReturnsAvailable,
  isWhiteGloveDeliveryAvailable,
]);
// Genesys chat section will always be true, hence '+1' is added
const availableColumnsCount = computed(() => columnsArr.value.filter((c) => c).length + 1);

const [isOpen, toggle] = useToggle(false);
const featuresText = !isMcom ? 'Features' : 'Product Features';
const productDetailsText = isMcom ? 'Product details' : 'Product Details';
const buttonText = computed(() => (isOpen.value ? 'View less' : 'View more'));
const viewDetailsText = computed(() => (isOpen.value ? 'View less details' : 'View full details'));

const additionalClasses = computed(() => ({
  'long-description-available': !!productDescription.value || !!longDescriptionFromColumns.value,
  'three-columns': availableColumnsCount.value === 3,
  'more-than-three': availableColumnsCount.value > 3,
  'less-than-three': availableColumnsCount.value < 3,
  'less-than-two': availableColumnsCount.value < 2,
}));

function getSwitchButtonAvailability() {
  if (isMobile.value) return true;

  const wrapperClientHeight = wrapper.value.clientHeight || 0;
  return wrapperClientHeight > CLOSE_WRAPPER_HEIGHT;
}

const showProductDetailsDrawer = () => {
  showSlideout({
    id: 'product-details-drawer',
    title: productDetailsText,
    component: defineAsyncComponent(
      /* istanbul ignore next */ () => import(/* webpackChunkName: "ProductDetailsDrawer" */ './slideouts/ProductDetailsDrawer.vue'),
    ),
    backable: false,
    props: {
      productId,
      product,
    },
  });
};

/** @type import('vue').Ref */
const productDetailsContainer = ref(null);

const handleButtonClick = () => {
  if (!isMobile.value) {
    showProductDetailsDrawer();
    tagViewMoreViewLessToggle(true);
  } else {
    toggle(!isOpen.value);

    if (!isOpen.value) {
      productDetailsContainer.value?.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  }
};

/** @param {MouseEvent} event */
const handleWrapperClick = (event) => {
  if (!isSwitchButtonAvailable.value) return;

  const { target } = event;
  if (target instanceof Element && target.closest('a, button')) return;

  showProductDetailsDrawer();
  tagViewMoreViewLessToggle(true);
};

const blockKeyboardNavigation = () => {
  if (!wrapper.value) return;

  const disableFocusableElements = () => {
    if (!wrapper.value) return;

    const FOCUSABLE_SELECTOR = 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusables = wrapper.value.querySelectorAll(FOCUSABLE_SELECTOR);

    focusables.forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
  };

  disableFocusableElements();

  // this runs once post-mount to disable focusables, then disconnect for perf.
  const mutationObserver = new MutationObserver(() => {
    disableFocusableElements();
    mutationObserver.disconnect();
  });

  mutationObserver.observe(wrapper.value, {
    childList: true,
    subtree: true,
  });
};

watch(isOpen, () => {
  tagViewMoreViewLessToggle(isOpen.value);
});

useResizeObserver(wrapper, () => {
  isSwitchButtonAvailable.value = getSwitchButtonAvailability();
});

useResizeObserver(genesysChatContainer, () => {
  isSwitchButtonAvailable.value = getSwitchButtonAvailability();
  if (isSwitchButtonAvailable.value && !isOpen.value) {
    blockKeyboardNavigation();
  }
});

watch(isSwitchButtonAvailable, (newValue) => {
  if (newValue && !isOpen.value) {
    nextTick(blockKeyboardNavigation);
  }
});

onMounted(async () => {
  isSwitchButtonAvailable.value = getSwitchButtonAvailability();

  if (isSwitchButtonAvailable.value) {
    await nextTick();

    blockKeyboardNavigation();
  }
});
</script>

<style lang="scss">
#product-details {
  ul.details {
    list-style: disc;

    ul {
      list-style: disc;
    }
  }
  .capitalize {
    text-transform: capitalize;
  }
}
</style>

<style lang="scss" scoped>
@import '~settingSCSS';

#product-details {
  :deep(.salesfloor) {
    // Rewrite salesfloor styles
    margin-top: 0px !important;

    .sf-virtual-advisor--expanded {
      margin-top: 0px !important;
    }

    .sf-virtual-advisor--stylist-title {
      @include body(medium);
    }
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }

  .product-id {
    color: $gray-1-color;
  }

  .wrapper {
    display: grid;
    grid-template-areas:
      'longDescription'
      'details';
    grid-template-columns: 1fr;
    background-color: $background-color;
    @include padding-left-xxs;
    @include padding-right-xxs;
    @include padding-top-xs;
    @include padding-bottom-xs;
  }

  .details {
    list-style: none;
    margin: 0;
    padding: 0;
    grid-area: details;
    display: grid;
    grid-template-columns: 1fr;
    gap: rem-calc(16px);
  }

  .long-description {
    grid-area: longDescription;
  }
}

@include breakpoint(small up) {
  #product-details {
    .expandable-wrapper {
      height: var(--blur-height);
      overflow: hidden;
      position: relative;
      cursor: pointer;

      &.open {
        height: auto;

        .switch-wrapper {
          @include padding-bottom-xs;
          position: relative;
          height: auto;
          display: flex;
          align-items: center;
          justify-content: center;
          background: $background-color;
          cursor: default;
        }
      }

      &:not(.open) {
        .switch-wrapper {
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 96px;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.5) 28.13%, #fff 72.4%);
          pointer-events: none;

          &.ai-prompts {
            height: 60px;
          }
          button {
            pointer-events: auto;
          }
        }

        .switch {
          position: absolute;
          bottom: 0;
        }
      }
    }
  }
}

@include breakpoint(small only) {
  #product-details {
    &.google-ai-enabled {
      .expandable-wrapper.open {
        @include margin-bottom-s;
      }
    }
    .product-id {
      @include body(small);
      color: $gray-1-color;
    }

    .long-description {
      @include margin-bottom-xs;
    }

    .expandable-wrapper {
      &.open {
        .switch-wrapper {
          @include padding-left-xxs;
          justify-content: flex-start;
        }
      }

      &:not(.open) {
        .switch {
          position: absolute;
          bottom: 0;
          left: 8px;
        }
      }
    }
  }
}

@include breakpoint(medium up) {
  #product-details {
    .switch {
      cursor: pointer;
    }

    .wrapper {
      grid-template-areas:
        'longDescription longDescription'
        'details details';
      column-gap: 0;
      @include padding-left-xs;
      @include padding-right-xs;
      @include padding-top-s;
      @include padding-bottom-s;

      &.long-description-available {
        row-gap: rem-calc(24px);
        column-gap: rem-calc(24px);
        grid-template-columns: 1fr 2fr;
        &.more-than-three,
        &.three-columns {
          grid-template-columns: 1fr 3fr;
        }
        &.less-than-two {
          grid-template-columns: 1fr 1fr;

          .details {
            grid-template-columns: 1fr;
          }
        }
      }
    }

    .details {
      gap: rem-calc(24px);
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

@include breakpoint(large up) {
  #product-details {
    .wrapper {
      grid-template-areas: 'longDescription details';
      column-gap: 0px;

      .details {
        grid-template-columns: repeat(3, 1fr);
      }

      &:not(.long-description-available) {
        grid-template-areas: 'details details';

        &.less-than-two {
          row-gap: 0;

          .details {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        &.less-than-three {
          .details {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        &.more-than-three {
          .details {
            grid-template-columns: repeat(4, 1fr);
          }
        }
      }

      &.long-description-available {
        row-gap: 0;
        &.less-than-two,
        &.less-than-three {
          grid-template-columns: 1fr 2fr;

          .details {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      }
    }
  }
}
</style>

<style lang="scss" scoped brand="mcom">
@import '~settingSCSS';

@include breakpoint(small only) {
  #product-details {
    .title {
      @include header(h4);
    }
  }
}
</style>

<style lang="scss" scoped brand="bcom">
@import '~settingSCSS';

#product-details {
  .column h4 {
    @include body(medium);
    text-transform: capitalize;
  }

  &.uppercase-titles {
    h4 {
      @include body(small);
      text-transform: uppercase;
      font-weight: bold;
    }
  }
}

@include breakpoint(small only) {
  #product-details {
    .title {
      @include header(h4);
    }
  }
  #product-details.details-optimization {
    .title {
      @include header(h4);
    }
  }
}
</style>

<style lang="scss" brand="bcom">
@import '~settingSCSS';

#product-details.uppercase-titles {
  b {
    @include body(small);
    text-transform: uppercase;
    font-weight: bold;
  }
}
</style>
